/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-props-no-spreading */
import React, { BaseSyntheticEvent, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Input, useTheme } from "@mui/material";
import { narrowPadding, useGetWidthWithOverflow } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { compareAsc } from "date-fns";
import { TimeCellEditorParams, TimeCellEditorRef, IventisTableData, RowNetworkStatus } from "../types/data-table.types";

const timeCellEditorToastKey = "TIME_CELL_EDITOR_NOTIFICATION";

export const TimeCellEditor = forwardRef(_TimeCellEditor);

/**
 * Used as cellEditor for data table column of type "text"
 */
// eslint-disable-next-line no-underscore-dangle
function _TimeCellEditor<TData extends IventisTableData = IventisTableData>(
    { getTimeCellValue, validator, node, context, required, ...props }: TimeCellEditorParams<TData>,
    ref: React.ForwardedRef<TimeCellEditorRef>
) {
    const [hours, setHours] = useState<string>(props.value?.displayText?.split(":")[0] || "00");
    const [mins, setMins] = useState<string>(props.value?.displayText?.split(":")[1] || "00");
    const [seconds, setSeconds] = useState<string>(props.value?.displayText?.split(":")[2] || "00");

    const { translate, timezone } = context;

    const newValue = getTimeCellValue(hours, mins, seconds, node, timezone);

    const theme = useTheme<Theme>();

    const invalidTime = validator(node, props.colDef.field as keyof TData, newValue as TData[keyof TData]);

    useEffect(() => {
        if (invalidTime.doesFail) {
            context.toast.error({ title: translate("Error"), message: translate(invalidTime.errorMessage) }, { key: timeCellEditorToastKey });
        }
    }, [invalidTime.doesFail]);

    const { ref: inputRef, width } = useGetWidthWithOverflow<HTMLDivElement>(null);

    useEffect(() => {
        props.columnApi.setColumnWidth(props.column, width);
    }, [width]);

    useImperativeHandle(ref, () => ({
        getValue() {
            return newValue;
        },
        isCancelBeforeStart() {
            // If the field is not required or has a value, while we are pending creation, make this cell uneditable
            const blockEditsUntilCreatedOnServer = (!required || props.value != null) && node.data.networkStatus === RowNetworkStatus.PendingCreation;
            return !node.data.canEdit || blockEditsUntilCreatedOnServer;
        },
        isCancelAfterEnd() {
            return invalidTime.doesFail || compareAsc(newValue?.value, props.value?.value) === 0;
        },
        isPopup() {
            return true;
        },
    }));

    return (
        <Input
            ref={inputRef}
            id="time"
            type="time"
            style={{ paddingLeft: narrowPadding, backgroundColor: theme.dataTableTheme.background, height: "100%", width: "100%" }}
            autoFocus
            value={`${hours}:${mins}:${seconds}`}
            onChange={(e: BaseSyntheticEvent) => {
                const time = e.target.value.split(":");
                setHours(time[0]);
                setMins(time[1]);
                setSeconds(time[2]);
            }}
            inputProps={{
                step: 1, // 1 second time interval
            }}
        />
    );
}
